import MuleBadge from '../../components/MuleBadge';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import OTRByLocationChainDrawer from '../../components/OTRByLocationChainDrawer';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import { fetchDriverEndpoints } from '../../services/driverEndpoints';
import type { DriverEndpointRow } from '../../types/driverEndpoints';
import { Tooltip } from '../../components/ui/Tooltip';
import type { QueuedLeg } from '../../types/driverEndpoints';
import MultiSelect, { type MultiSelectOption } from '../../components/MultiSelect';
import DateTimePicker from '../../components/DateTimePicker';
import FilterBar from '../../components/FilterBar';

const REFRESH_INTERVAL = 30000;

function formatCityState(loc: { city: string | null; state: string | null }): string {
  if (!loc.city && !loc.state) return '—';
  if (!loc.state) return loc.city ?? '—';
  if (!loc.city) return loc.state;
  return `${loc.city}, ${loc.state}`;
}

function formatFreeAt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function legStatusBadgeClass(status: string): string {
  const s = (status || '').toUpperCase().trim();
  if (s === 'DEST DEPARTED') return 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300';
  if (s === 'OUTGATED' || s === 'ORIG ARRIVED' || s === 'ORIG DEPARTED' || s === 'DEST ARRIVED') {
    return 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300';
  }
  // ON TIME, LATE, '', anything else → Dispatched / gray
  return 'bg-gray-200 text-label hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600';
}

function toYmd(d: Date | undefined): string {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function legTooltip(leg: QueuedLeg) {
  return (
    <div className="text-xs">
      <div className="font-semibold">PRO {leg.pronumber}-{leg.legnum}</div>
      <div>{leg.legstatus}</div>
      <div>
        <span>{formatCityState(leg.origin)}</span>
        <span className="mx-1">→</span>
        <span>{formatCityState(leg.destination)}</span>
      </div>
      <div>Appt: {formatFreeAt(leg.appt_at)}</div>
      {leg.is_otr && <div className="font-semibold text-orange-300">OTR</div>}
    </div>
  );
}

export default function OTRByLocation() {
  const [rows, setRows] = useState<DriverEndpointRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [cityFilter, setCityFilter] = useState<Set<string>>(new Set());
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<string | null>(null);

  const reqIdRef = useRef(0);
  const load = useCallback(async (silent = false) => {
    const myId = ++reqIdRef.current;
    if (!silent) setLoading(true);
    try {
      const data = await fetchDriverEndpoints();
      if (myId !== reqIdRef.current) return;
      setRows(data.drivers);
      setError(null);
    } catch (e: any) {
      if (myId !== reqIdRef.current) return;
      setError(e?.message ?? 'Failed to load driver endpoints');
    } finally {
      if (!silent && myId === reqIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useVisibilityAwareInterval(() => {
    load(true);
  }, REFRESH_INTERVAL);

  const availableStates = useMemo<MultiSelectOption[]>(() => {
    const s = new Set<string>();
    rows.forEach((r) => { if (r.final_destination.state) s.add(r.final_destination.state); });
    return Array.from(s).sort().map((v) => ({ value: v, label: v }));
  }, [rows]);

  const availableCities = useMemo<MultiSelectOption[]>(() => {
    const inState = (r: DriverEndpointRow) =>
      stateFilter.length === 0 ||
      (r.final_destination.state && stateFilter.includes(r.final_destination.state));
    const c = new Set<string>();
    rows.forEach((r) => {
      if (!inState(r)) return;
      if (r.final_destination.city) c.add(r.final_destination.city);
    });
    return Array.from(c).sort().map((v) => ({ value: v, label: v }));
  }, [rows, stateFilter]);

  useEffect(() => {
    if (cityFilter.size === 0) return;
    const avail = new Set(availableCities.map((o) => o.value));
    let changed = false;
    const next = new Set<string>();
    cityFilter.forEach((c) => {
      if (avail.has(c)) next.add(c);
      else changed = true;
    });
    if (changed) setCityFilter(next);
  }, [availableCities, cityFilter]);

  const filteredRows = useMemo(() => {
    const startYmd = toYmd(startDate ?? undefined);
    const endYmd = toYmd(endDate ?? undefined);
    return rows.filter((r) => {
      if (stateFilter.length > 0 && (!r.final_destination.state || !stateFilter.includes(r.final_destination.state))) return false;
      if (cityFilter.size > 0 && (!r.final_destination.city || !cityFilter.has(r.final_destination.city))) return false;
      if (startYmd && (!r.free_at || r.free_at < startYmd)) return false;
      if (endYmd && (!r.free_at || r.free_at > endYmd + 'T23:59:59')) return false;
      return true;
    });
  }, [rows, stateFilter, cityFilter, startDate, endDate]);

  const columns = useMemo<ColumnDef<DriverEndpointRow>[]>(() => [
    {
      id: 'driver',
      header: 'Driver',
      accessorFn: (row) => `${row.driver} ${row.driver_name}`.trim(),
      enableSorting: true,
      cell: ({ row }) => (
        <span>
          <span className="font-bold">{row.original.driver}<MuleBadge isMule={row.original.isMule} /></span>
          {row.original.driver_name && (
            <span className="ml-2 text-gray-600 dark:text-white">{row.original.driver_name}</span>
          )}
        </span>
      ),
    },
    {
      id: 'current',
      header: 'Current Location',
      accessorFn: (row) => formatCityState(row.current_location),
      enableSorting: true,
    },
    {
      id: 'finalState',
      header: 'Final State',
      accessorFn: (row) => row.final_destination.state ?? '',
      enableSorting: true,
    },
    {
      id: 'finalCity',
      header: 'Final City',
      accessorFn: (row) => row.final_destination.city ?? '',
      enableSorting: true,
    },
    {
      id: 'freeAt',
      header: 'Free At',
      accessorFn: (row) => row.free_at ?? '',
      enableSorting: true,
      cell: ({ row }) => formatFreeAt(row.original.free_at),
    },
    {
      id: 'queued',
      header: 'Queued Legs',
      accessorFn: (row) => row.queued_legs.map((l) => `${l.pronumber}-${l.legnum}`).join(' '),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.queued_legs.map((leg) => (
            <Tooltip key={`${leg.pronumber}-${leg.legnum}`} content={legTooltip(leg)}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDriver(row.original.driver);
                }}
                className={`rounded px-1.5 py-0.5 text-xs ${legStatusBadgeClass(leg.legstatus)}`}
              >
                {leg.pronumber}-{leg.legnum}
              </button>
            </Tooltip>
          ))}
        </div>
      ),
    },
  ], []);

  return (
    <div className="flex flex-col h-full">
      <FilterBar>
        <MultiSelect
          options={availableStates}
          value={stateFilter}
          onChange={setStateFilter}
          placeholder="All states"
          enableSearch
          showClearOption
          clearOptionLabel="All states"
          size="sm"
          usePortal
          className="min-w-[12rem]"
        />
        <MultiSelect
          options={availableCities}
          value={Array.from(cityFilter)}
          onChange={(values) => setCityFilter(new Set(values))}
          placeholder="All cities"
          enableSearch
          showClearOption
          clearOptionLabel="All cities"
          size="sm"
          usePortal
          className="min-w-[12rem]"
        />
        <FilterBar.Divider />
        <FilterBar.Group label="Free between">
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={startDate}
            onChange={(val) => setStartDate((val as Date | null) ?? null)}
            inputVariant="compact"
            labelType="none"
            placeholder="Start"
            autoApply
          />
          <span className="text-xs text-muted">and</span>
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={endDate}
            onChange={(val) => setEndDate((val as Date | null) ?? null)}
            inputVariant="compact"
            labelType="none"
            placeholder="End"
            autoApply
          />
        </FilterBar.Group>
        {(stateFilter.length > 0 || cityFilter.size > 0 || startDate || endDate) && (
          <button
            type="button"
            onClick={() => {
              setStateFilter([]);
              setCityFilter(new Set());
              setStartDate(null);
              setEndDate(null);
            }}
            className="rounded border border-gray-300 bg-panel px-2 py-0.5 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            Clear filters
          </button>
        )}
      </FilterBar>

      {error && (
        <div className="px-6 py-2 text-sm text-red-700 bg-red-50 border-b border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800">
          {error}
        </div>
      )}

      <div className="flex-1 min-h-0 p-4">
        <TanTable
          data={filteredRows}
          columns={columns}
          loading={loading}
          settingsKey="otr-by-location"
          cardTitle="OTR By Location"
          searchPlaceholder="Search drivers..."
          enableColumnFilters={true}
          enableSearch={true}
          enableExcelExport={true}
          defaultPageSize={100}
          defaultSorting={[{ id: 'finalState', desc: false }, { id: 'finalCity', desc: false }]}
          onRowClick={(row: DriverEndpointRow) => setSelectedDriver(row.driver)}
          fillContainer={true}
        />
      </div>

      <OTRByLocationChainDrawer
        driver={selectedDriver}
        onClose={() => setSelectedDriver(null)}
      />
    </div>
  );
}
